/**
 * Root Application Component — GenesisBox
 * OAuth-based org access with per-org storage backends.
 */

import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';
import type { ReactNode } from 'react';

import { AuthProvider } from '@contexts/AuthContext';
import { OrgProvider } from '@contexts/OrgContext';
import { StorageScopeProvider } from '@contexts/StorageScopeContext';
import { TransferQueueProvider } from '@contexts/TransferQueueContext';
import {
  LandingPage,
  LoginPage,
  DrivePage,
  AuthCallbackPage,
  DrivePdfViewerPage,
  EmbedRedirectPage,
  PublicDriveEmbedPage,
  StandaloneEmbedShellPage,
  SetupGuidePage,
} from '@pages/index';
import { isLegacyViewerForced } from '@config/featureFlags';
import { useAuth } from '@hooks/auth/useAuth';

const globalStyles = (
  <GlobalStyles
    styles={{
      ':root': {
        colorScheme: 'dark',
      },
      'html, body, #root': {
        height: '100%',
      },
      body: {
        margin: 0,
        backgroundColor: '#0e0e10',
        backgroundImage: `
          radial-gradient(circle at top left, rgba(143, 245, 255, 0.1), transparent 30%),
          radial-gradient(circle at top right, rgba(255, 81, 250, 0.1), transparent 24%),
          linear-gradient(180deg, #0b0b0d 0%, #101014 100%)
        `,
        color: '#f9f5f8',
        fontFamily: '"Inter", sans-serif',
      },
      '*': {
        boxSizing: 'border-box',
      },
      '.font-label': {
        fontFamily: '"Space Grotesk", sans-serif',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
      },
      '.neon-glow-text': {
        textShadow: '0 0 14px rgba(143, 245, 255, 0.45)',
      },
      '.glass-card': {
        background: 'rgba(25, 25, 28, 0.72)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(143, 245, 255, 0.08)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
        transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
      },
      '.glass-card:hover': {
        borderColor: 'rgba(143, 245, 255, 0.28)',
        boxShadow: '0 0 0 1px rgba(143, 245, 255, 0.1), 0 24px 60px rgba(0, 0, 0, 0.45)',
      },
      '::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },
      '::-webkit-scrollbar-track': {
        background: '#0e0e10',
      },
      '::-webkit-scrollbar-thumb': {
        background: '#262528',
        borderRadius: '999px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#8ff5ff',
      },
    }}
  />
);

const theme = createTheme({
  shape: {
    borderRadius: 12,
  },
  palette: {
    mode: 'dark',
    primary: { main: '#8ff5ff', contrastText: '#003f43' },
    secondary: { main: '#ff51fa' },
    background: {
      default: '#0e0e10',
      paper: '#19191c',
    },
    text: {
      primary: '#f9f5f8',
      secondary: '#adaaad',
    },
    warning: { main: '#b190ff' },
    success: { main: '#00deec' },
    info: { main: '#8ff5ff' },
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 700,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(25, 25, 28, 0.72)',
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
});

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to={`/login${location.search}`} replace />;
  return <>{children}</>;
}

/**
 * Normalize a URL search string before parsing. Defends against URLs that
 * were copy-pasted out of an HTML attribute and arrived with `&amp;`
 * separators (real example: a user shared `?view=pdf&amp;share=xyz`,
 * which URLSearchParams reads as the key `amp;share` and silently fails
 * the public-route check, redirecting to login).
 */
function normalizeSearch(search: string): string {
  return (search || '').replace(/&amp;/g, '&');
}

function isPublicEmbedRoute(search: string): boolean {
  const params = new URLSearchParams(normalizeSearch(search));
  return params.has('share') || params.has('embed');
}

function DriveRootRoute() {
  const location = useLocation();
  if (isPublicEmbedRoute(location.search)) return <PublicDriveEmbedPage />;
  return (
    <ProtectedRoute>
      <DrivePage />
    </ProtectedRoute>
  );
}

function DriveFileRoute() {
  const location = useLocation();
  if (isPublicEmbedRoute(location.search)) return <PublicDriveEmbedPage />;
  return (
    <ProtectedRoute>
      <DrivePdfViewerPage />
    </ProtectedRoute>
  );
}

/**
 * Public front door. Logged-out visitors get the marketing landing page;
 * authenticated visitors skip straight to their drive. Preserve the query
 * string on redirect so deep-link params (e.g. ?folder=) survive.
 */
function RootRoute() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (isAuthenticated) return <Navigate to={`/drive${location.search}`} replace />;
  return <LandingPage />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      <BrowserRouter>
        <AuthProvider>
          <OrgProvider>
            <StorageScopeProvider>
              <TransferQueueProvider>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route
                    path="/embed/:shareId"
                    element={
                      isLegacyViewerForced()
                        ? <EmbedRedirectPage />
                        : <StandaloneEmbedShellPage />
                    }
                  />
                  <Route path="/drive" element={<DriveRootRoute />} />
                  <Route path="/drive/*" element={<DriveFileRoute />} />
                  <Route
                    path="/admin/setup-guide"
                    element={
                      <ProtectedRoute>
                        <SetupGuidePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/" element={<RootRoute />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </TransferQueueProvider>
            </StorageScopeProvider>
          </OrgProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
